<template>
  <div class="invoice-list-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">
          <i class="fas fa-file-invoice-dollar"></i>
          Invoices
        </h1>
        <p class="page-subtitle">Manage and generate client invoices</p>
      </div>
      <button class="btn btn-primary" @click="$router.push('/invoicing/new')">
        <i class="fas fa-plus"></i>
        New Invoice
      </button>
    </div>

    <BankConnectionPanel @invoice-paid="loadInvoices" />

    <!-- Filters -->
    <div class="filters-bar">
      <div class="filter-group">
        <input
          v-model="searchQuery"
          type="text"
          class="filter-input"
          placeholder="Search by client or invoice #..."
        />
      </div>
      <div class="filter-group">
        <select v-model="statusFilter" class="filter-select">
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="SENT">Sent</option>
          <option value="PAID">Paid</option>
          <option value="OVERDUE">Overdue</option>
          <option value="VOID">Void</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="clientFilter" class="filter-select">
          <option value="">All Clients</option>
          <option v-for="client in clients" :key="client.id" :value="client.id">
            {{ client.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Stats Row -->
    <div class="stats-row" v-if="!loading">
      <div class="stat-card">
        <div class="stat-value">{{ filteredInvoices.length }}</div>
        <div class="stat-label">Invoices</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${{ totalBilled.toFixed(2) }}</div>
        <div class="stat-label">Total Billed</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${{ totalPaid.toFixed(2) }}</div>
        <div class="stat-label">Collected</div>
      </div>
      <div class="stat-card">
        <div class="stat-value stat-outstanding">${{ totalOutstanding.toFixed(2) }}</div>
        <div class="stat-label">Outstanding</div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div v-for="n in 5" :key="n" class="skeleton-row"></div>
    </div>

    <!-- Invoice Table -->
    <div v-else-if="filteredInvoices.length > 0" class="invoice-table-wrapper">
      <table class="invoice-table">
        <thead>
          <tr>
            <th @click="setSortField('invoice_number')" class="sortable">
              Invoice # <i :class="sortIcon('invoice_number')"></i>
            </th>
            <th @click="setSortField('client_organization_name')" class="sortable">
              Client <i :class="sortIcon('client_organization_name')"></i>
            </th>
            <th @click="setSortField('issue_date')" class="sortable">
              Issue Date <i :class="sortIcon('issue_date')"></i>
            </th>
            <th>Due Date</th>
            <th @click="setSortField('total')" class="sortable right-align">
              Total <i :class="sortIcon('total')"></i>
            </th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="invoice in paginatedInvoices"
            :key="invoice.id"
            @click="$router.push(`/invoicing/${invoice.id}`)"
            class="invoice-row"
          >
            <td>
              <span class="invoice-number">{{ invoice.invoice_number }}</span>
            </td>
            <td>
              <span class="client-name">{{ invoice.client_organization_name }}</span>
            </td>
            <td>{{ formatDate(invoice.issue_date) }}</td>
            <td>
              <span :class="{ 'overdue-date': isOverdue(invoice) }">
                {{ invoice.due_date ? formatDate(invoice.due_date) : '—' }}
              </span>
            </td>
            <td class="right-align">
              <strong>${{ parseFloat(invoice.total).toFixed(2) }}</strong>
            </td>
            <td>
              <span class="status-badge" :class="`status-${invoice.status}`">
                {{ invoice.status }}
              </span>
            </td>
            <td @click.stop>
              <div class="action-buttons">
                <button
                  class="btn-icon"
                  title="Edit"
                  @click="$router.push(`/invoicing/${invoice.id}/edit`)"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button
                  class="btn-icon"
                  title="Generate PDF"
                  @click="generatePDF(invoice)"
                  :disabled="generatingPDF === invoice.id"
                >
                  <i :class="generatingPDF === invoice.id ? 'fas fa-spinner fa-spin' : 'fas fa-file-pdf'"></i>
                </button>
                <button
                  v-if="invoice.has_pdf"
                  class="btn-icon btn-icon-success"
                  title="Download PDF"
                  @click="downloadPDF(invoice)"
                >
                  <i class="fas fa-download"></i>
                </button>
                <button
                  v-if="invoice.status === 'DRAFT'"
                  class="btn-icon btn-icon-primary"
                  title="Mark Sent"
                  @click="markSent(invoice)"
                >
                  <i class="fas fa-paper-plane"></i>
                </button>
                <button
                  v-if="invoice.status === 'SENT'"
                  class="btn-icon btn-icon-success"
                  title="Mark Paid"
                  @click="markPaid(invoice)"
                >
                  <i class="fas fa-check-circle"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="totalPages > 1">
        <button :disabled="currentPage === 1" @click="currentPage--" class="page-btn">
          <i class="fas fa-chevron-left"></i>
        </button>
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button :disabled="currentPage === totalPages" @click="currentPage++" class="page-btn">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <i class="fas fa-file-invoice-dollar empty-icon"></i>
      <h3>No invoices yet</h3>
      <p>Create your first invoice to get started.</p>
      <button class="btn btn-primary" @click="$router.push('/invoicing/new')">
        <i class="fas fa-plus"></i>
        Create Invoice
      </button>
    </div>
  </div>
</template>

<script>
import { UI_CONSTANTS } from '@/constants';
import api from '@/services/api';
import BankConnectionPanel from '@/components/plaid/BankConnectionPanel.vue';

export default {
  name: 'InvoiceList',
  components: { BankConnectionPanel },

  data() {
    return {
      invoices: [],
      clients: [],
      loading: true,
      searchQuery: '',
      statusFilter: '',
      clientFilter: '',
      sortField: 'created_at',
      sortDir: 'desc',
      currentPage: 1,
      pageSize: UI_CONSTANTS.DEFAULT_PAGE_SIZE || 15,
      generatingPDF: null,
    };
  },

  computed: {
    filteredInvoices() {
      let result = [...this.invoices];
      const q = this.searchQuery.toLowerCase();
      if (q) {
        result = result.filter(
          (i) =>
            i.invoice_number?.toLowerCase().includes(q) ||
            i.client_organization_name?.toLowerCase().includes(q)
        );
      }
      if (this.statusFilter) {
        result = result.filter((i) => i.status === this.statusFilter);
      }
      if (this.clientFilter) {
        result = result.filter((i) => i.client_organization === this.clientFilter);
      }
      return result.sort((a, b) => {
        let va = a[this.sortField] ?? '';
        let vb = b[this.sortField] ?? '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return this.sortDir === 'asc' ? -1 : 1;
        if (va > vb) return this.sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    },

    totalPages() {
      return Math.ceil(this.filteredInvoices.length / this.pageSize);
    },

    paginatedInvoices() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredInvoices.slice(start, start + this.pageSize);
    },

    totalBilled() {
      return this.filteredInvoices.reduce((s, i) => s + parseFloat(i.total || 0), 0);
    },

    totalPaid() {
      return this.filteredInvoices
        .filter((i) => i.status === 'PAID')
        .reduce((s, i) => s + parseFloat(i.total || 0), 0);
    },

    totalOutstanding() {
      return this.filteredInvoices
        .filter((i) => ['SENT', 'OVERDUE'].includes(i.status))
        .reduce((s, i) => s + parseFloat(i.total || 0), 0);
    },
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
    clientFilter() {
      this.currentPage = 1;
    },
  },

  async mounted() {
    await Promise.all([this.loadInvoices(), this.loadClients()]);
  },

  methods: {
    async loadInvoices() {
      this.loading = true;
      try {
        const response = await api.get('/invoices/');
        this.invoices = response.data.results || response.data;
      } catch (e) {
        console.error('Failed to load invoices', e);
      } finally {
        this.loading = false;
      }
    },

    async loadClients() {
      try {
        const response = await api.get('/invoicing/clients/');
        this.clients = response.data;
      } catch (e) {
        console.error('Failed to load clients', e);
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },

    isOverdue(invoice) {
      if (!invoice.due_date || invoice.status === 'PAID') return false;
      return new Date(invoice.due_date) < new Date();
    },

    setSortField(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDir = 'asc';
      }
    },

    sortIcon(field) {
      if (this.sortField !== field) return 'fas fa-sort sort-inactive';
      return this.sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    async generatePDF(invoice) {
      this.generatingPDF = invoice.id;
      try {
        await api.post(`/invoices/${invoice.id}/generate-pdf/`);
        await this.loadInvoices();
      } catch (e) {
        alert('PDF generation failed: ' + (e.response?.data?.detail || e.message));
      } finally {
        this.generatingPDF = null;
      }
    },

    async downloadPDF(invoice) {
      try {
        const response = await api.get(`/invoices/${invoice.id}/download-pdf/`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Invoice-${invoice.invoice_number || invoice.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        alert('Download failed: ' + (e.response?.data?.detail || e.message));
      }
    },

    async markSent(invoice) {
      try {
        await api.post(`/invoices/${invoice.id}/mark-sent/`);
        await this.loadInvoices();
      } catch (e) {
        alert('Failed to update invoice');
      }
    },

    async markPaid(invoice) {
      try {
        await api.post(`/invoices/${invoice.id}/mark-paid/`);
        await this.loadInvoices();
      } catch (e) {
        alert('Failed to update invoice');
      }
    },
  },
};
</script>

<style scoped>
.invoice-list-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title i { color: var(--primary, #2c3e8c); }
.page-subtitle { color: var(--text-secondary, #6c7a9c); margin-top: 4px; font-size: 0.9rem; }

.btn {
  padding: 8px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s;
}

.btn-primary {
  background: var(--primary, #2c3e8c);
  color: #fff;
}
.btn-primary:hover { background: var(--primary-dark, #1e2d6e); }

.filters-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filter-input,
.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 8px;
  font-size: 0.875rem;
  background: #fff;
  color: var(--text-primary, #1a1a2e);
  min-width: 180px;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: var(--primary, #2c3e8c);
}

.stats-row {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.stat-card {
  flex: 1;
  min-width: 120px;
  background: #fff;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 10px;
  padding: 16px 20px;
  text-align: center;
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
}

.stat-outstanding { color: #e65100; }
.stat-label { font-size: 0.75rem; color: var(--text-secondary, #6c7a9c); text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; }

.loading-container { display: flex; flex-direction: column; gap: 12px; }
.skeleton-row {
  height: 52px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.invoice-table-wrapper {
  background: #fff;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 12px;
  overflow: hidden;
}

.invoice-table {
  width: 100%;
  border-collapse: collapse;
}

.invoice-table thead tr {
  background: #f8f9ff;
}

.invoice-table thead th {
  padding: 12px 16px;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary, #6c7a9c);
  border-bottom: 1px solid var(--border, #e0e4f0);
}

.sortable { cursor: pointer; user-select: none; }
.sortable:hover { color: var(--primary, #2c3e8c); }
.sort-inactive { opacity: 0.3; }
.right-align { text-align: right; }

.invoice-row {
  cursor: pointer;
  transition: background 0.1s;
}
.invoice-row:hover { background: #f8f9ff; }
.invoice-row td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border, #e0e4f0);
  font-size: 0.875rem;
}
.invoice-row:last-child td { border-bottom: none; }

.invoice-number {
  font-weight: 600;
  color: var(--primary, #2c3e8c);
  font-family: monospace;
}

.client-name { font-weight: 500; }
.overdue-date { color: #e65100; font-weight: 600; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-DRAFT   { background: #f0f0f0; color: #666; }
.status-SENT    { background: #e3f2fd; color: #1565c0; }
.status-PAID    { background: #e8f5e9; color: #2e7d32; }
.status-VOID    { background: #fce4ec; color: #c62828; }
.status-OVERDUE { background: #fff3e0; color: #e65100; }

.action-buttons { display: flex; gap: 4px; align-items: center; }

.btn-icon {
  background: none;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary, #6c7a9c);
  font-size: 0.8rem;
  transition: all 0.15s;
}

.btn-icon:hover { background: #f0f3ff; color: var(--primary, #2c3e8c); border-color: var(--primary, #2c3e8c); }
.btn-icon:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-icon-primary { color: var(--primary, #2c3e8c); }
.btn-icon-success { color: #2e7d32; }

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px;
  border-top: 1px solid var(--border, #e0e4f0);
}

.page-btn {
  background: none;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}
.page-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.page-btn:not(:disabled):hover { background: #f0f3ff; border-color: var(--primary, #2c3e8c); }
.page-info { font-size: 0.875rem; color: var(--text-secondary, #6c7a9c); }

.empty-state {
  text-align: center;
  padding: 64px 24px;
  color: var(--text-secondary, #6c7a9c);
}

.empty-icon { font-size: 3.5rem; margin-bottom: 16px; opacity: 0.3; }
.empty-state h3 { font-size: 1.1rem; font-weight: 600; color: var(--text-primary, #1a1a2e); margin-bottom: 8px; }
.empty-state p { margin-bottom: 24px; }
</style>
